import { choiceFields } from "../protocol/choice";
import type { WireChoice } from "../protocol/protocol";

/**
 * The host side of the MCP Apps bridge (2026-01-26), the subset the approval view uses (V's
 * contract): answer ui/initialize, push the choice as tool-input and its settlement as
 * tool-result, turn a confirm_action call into our decide (with the card it was pressed on), follow
 * size-changed, and open the links the host allows (ui/open-link).
 * Pure: the frame wires `receive` to window messages from its iframe and `post` to it.
 */

export const MCP_APPS_PROTOCOL = "2026-01-26";
export const FRAME_HEIGHT = { min: 60, max: 800 } as const;

const METHOD_NOT_FOUND = -32601;
const INVALID_PARAMS = -32602;
/** A JSON-RPC server error: the host would not open that link. */
const LINK_REFUSED = -32000;
/** Told to the view in ui/initialize: this host's confirm_action takes a list card's ticked `rows`.
 * The view no longer reads it (an MCP host's confirm_action takes them too); kept for older views. */
export const ROWS_CAPABILITY = { "steddy/rows": {} } as const;

export interface BridgeHost {
  post(message: Record<string, unknown>): void;
  /**
   * The socket's choice.decide, with a list card's ticked rows; rejects with a message to show in
   * the view. `shown` is the view's own: the args_sha256 of the card it drew, which the server
   * refuses once the card has changed.
   */
  decide(option: string, rows?: string[], shown?: string): Promise<WireChoice>;
  setHeight(px: number): void;
  /** Opens a link the view asks for; false when the host won't. No host opener: every link refused. */
  openLink?(url: string): boolean;
  locale?: string;
  timeZone?: string;
  /** MCP Apps' platform now: "mobile" in the phone layout, where the view goes flat edge to edge. */
  platform?: () => Platform;
}

export type Platform = "web" | "mobile";

type Message = { jsonrpc: "2.0"; id?: number | string; method?: string; params?: unknown };

const isRecord = (value: unknown): value is Record<string, unknown> =>
  typeof value === "object" && value !== null && !Array.isArray(value);

function isMessage(value: unknown): value is Message {
  return isRecord(value) && value.jsonrpc === "2.0";
}

/** The choice as the view reads it: its id under `choice_id`, the chosen option as
 * `chosen_option`, its key/value grid as `fields`, and an expired choice as superseded (nothing
 * expires on screen). The approve label is already the approve option's label, which the view shows. */
export function viewChoice(choice: WireChoice): Record<string, unknown> {
  return {
    ...choice,
    choice_id: choice.ref,
    chosen_option: choice.chosen,
    status: choice.status === "expired" ? "cancelled" : choice.status,
    fields: choiceFields(choice),
  };
}

/** What the view is shown of a settled choice: its status, whether its call ran or failed, and
 * how each ticked row went (a run's call settles its rows after the press, as a later event). A
 * redrawn card (its args_sha256) goes to the view too, so what it draws is what a press decides. */
const shownKey = (choice: WireChoice) =>
  JSON.stringify([
    choice.status,
    choice.executed === true,
    choice.error ? "failed" : "",
    choiceFields(choice)
      .filter((field) => field.state)
      .map((field) => [field.key, field.state, field.line ?? ""]),
    choice.args_sha256 ?? "",
  ]);
const pendingKey = (choice: WireChoice) =>
  JSON.stringify(["pending", false, "", [], choice.args_sha256 ?? ""]);

export function createBridge(host: BridgeHost) {
  let choice: WireChoice | null = null;
  let initialized = false;
  /** What the view was last sent (`shownKey`), the receipt's "what it did". */
  let shown: string | null = null;
  /** The store's last status for the choice; a press's own answer never sets it. */
  let storeStatus: WireChoice["status"] | null = null;
  let reopened = false;
  /** The platform the view was last told (in ui/initialize, then each change). */
  let toldPlatform: Platform | undefined;

  const notify = (method: string, params: Record<string, unknown>) =>
    host.post({ jsonrpc: "2.0", method, params });
  const answer = (id: Message["id"], result: Record<string, unknown>) =>
    host.post({ jsonrpc: "2.0", id, result });
  const fail = (id: Message["id"], code: number, message: string) =>
    host.post({ jsonrpc: "2.0", id, error: { code, message } });

  /** Once the view listens, it hears the platform whenever it differs from what it was told. */
  function tellPlatform() {
    const platform = host.platform?.();
    if (!initialized || !platform || platform === toldPlatform) return;
    toldPlatform = platform;
    notify("ui/notifications/host-context-changed", { platform });
  }

  /**
   * Values wait for `initialized`; then the choice goes as input, and each change of its status,
   * of whether its call ran or of its rows' states as a result. Input goes again only when the store saw a settled choice
   * open again (its call failed elsewhere, or the sweep reopened it): the one signal the view takes
   * back to pending. A press here never does that, since the store has not seen it settled.
   */
  function push() {
    if (!initialized || !choice) return;
    if (shown === null || reopened) {
      notify("ui/notifications/tool-input", { arguments: viewChoice(choice) });
      shown = pendingKey(choice);
      reopened = false;
    }
    const key = shownKey(choice);
    if (key !== shown) {
      notify("ui/notifications/tool-result", {
        content: [],
        structuredContent: viewChoice(choice),
      });
      shown = key;
    }
  }

  function confirm(id: Message["id"], params: unknown) {
    const args = isRecord(params) && isRecord(params.arguments) ? params.arguments : null;
    const option = typeof args?.option === "string" ? args.option : null;
    // Left out: the rows as proposed. Which rows the card offers is the server's to check.
    const rows = args?.rows;
    const validRows =
      rows === undefined ||
      (Array.isArray(rows) && rows.every((key): key is string => typeof key === "string"));
    // Passed through as the view sent it: the server, not the bridge, compares it.
    const cardShown = args?.shown;
    const valid =
      validRows &&
      isRecord(params) &&
      params.name === "confirm_action" &&
      choice !== null &&
      args?.choice_id === choice.ref &&
      choice.options.some((candidate) => candidate.id === option) &&
      (cardShown === undefined || typeof cardShown === "string");
    if (!valid || !option) return fail(id, INVALID_PARAMS, "Unknown choice or option.");
    host.decide(option, rows as string[] | undefined, cardShown).then(
      (decided) => {
        choice = decided;
        shown = shownKey(decided); // the view settles itself from this answer
        answer(id, { content: [], structuredContent: viewChoice(decided) });
      },
      (error: { message?: string }) =>
        answer(id, {
          isError: true,
          content: [{ type: "text", text: error?.message || "That didn't go through. Try again." }],
        })
    );
  }

  const REQUESTS: Record<string, (id: Message["id"], params: unknown) => void> = {
    "ui/initialize": (id) => {
      toldPlatform = host.platform?.();
      answer(id, {
        protocolVersion: MCP_APPS_PROTOCOL,
        hostInfo: { name: "Steddy", version: "1" },
        hostCapabilities: { serverTools: {}, experimental: ROWS_CAPABILITY },
        // No style variables: the view's defaults are our tokens.
        hostContext: {
          theme: "light",
          displayMode: "inline",
          availableDisplayModes: ["inline"],
          ...(host.locale && { locale: host.locale }),
          ...(host.timeZone && { timeZone: host.timeZone }),
          ...(toldPlatform && { platform: toldPlatform }),
        },
      });
    },
    "tools/call": confirm,
    "ui/open-link": (id, params) => {
      const url = isRecord(params) && typeof params.url === "string" ? params.url : null;
      if (!url) return fail(id, INVALID_PARAMS, "No link to open.");
      if (!host.openLink?.(url)) return fail(id, LINK_REFUSED, "That link can't be opened here.");
      answer(id, {});
    },
    ping: (id) => answer(id, {}),
  };

  const NOTIFICATIONS: Record<string, (params: unknown) => void> = {
    "ui/notifications/initialized": () => {
      initialized = true;
      tellPlatform(); // a layout change between the two went untold
      push();
    },
    "ui/notifications/size-changed": (params) => {
      const height = isRecord(params) ? params.height : undefined;
      if (typeof height === "number" && Number.isFinite(height)) {
        host.setHeight(Math.min(FRAME_HEIGHT.max, Math.max(FRAME_HEIGHT.min, Math.ceil(height))));
      }
    },
  };

  return {
    /** One message from the frame (the caller has checked it came from our iframe). */
    receive(data: unknown) {
      if (!isMessage(data) || typeof data.method !== "string") return; // we send no requests
      const hasId = typeof data.id === "number" || typeof data.id === "string";
      if (hasId) {
        const handle = Object.hasOwn(REQUESTS, data.method) ? REQUESTS[data.method] : null;
        if (handle) handle(data.id, data.params);
        else fail(data.id, METHOD_NOT_FOUND, "Method not found");
      } else if (Object.hasOwn(NOTIFICATIONS, data.method)) {
        NOTIFICATIONS[data.method](data.params);
      }
    },
    /** The frame's choice as the store has it now. */
    choiceChanged(next: WireChoice | null) {
      if (storeStatus !== null && storeStatus !== "pending" && next?.status === "pending") {
        reopened = true;
      }
      storeStatus = next?.status ?? null;
      choice = next;
      push();
    },
    /** The layout changed (a resize, a rotation): an initialized view hears its new platform. */
    platformChanged: tellPlatform,
  };
}

export type Bridge = ReturnType<typeof createBridge>;
